import { vec3 } from 'gl-matrix';
import { Camera } from '../camera';
import { AudioBusId, AUDIO_BUS_IDS, AudioBusVolumes } from './AudioBus';
import { AudioClipLibrary } from './AudioClipLibrary';
import { AudioListener } from './AudioListener';
import { AudioResolver } from './AudioResolver';
import { AudioSettings, clampVolume, defaultAudioSettings, mergeAudioSettings } from './AudioSettings';
import { AudioSource, AudioSourceTarget } from './AudioSource';
import { Voice } from './Voice';
import { VoicePool } from './VoicePool';

export type AudioEngineInit = {
  /** Required: how to translate clipIds to URLs. */
  resolver: AudioResolver;
  /** Optional injection point for tests; defaults to new window.AudioContext. */
  ctx?: AudioContext;
  /** Optional pre-built library (e.g. test mock). Resolver is ignored if supplied. */
  library?: AudioClipLibrary;
  settings?: Partial<AudioSettings>;
};

export type PlayOneShotOptions = {
  /** World-space position (vec3). Omitted → 2D non-spatial. */
  position?: vec3 | number[];
  /** Live-attached target; if provided, position follows each update tick. */
  attachTo?: AudioSourceTarget;
  /** Defaults to `'sfx'`. */
  bus?: AudioBusId;
  /** Linear multiplier on top of clip.config.gain. Default 1. */
  volume?: number;
  /** playbackRate multiplier on top of clip.pickPitch(). Default 1. */
  pitch?: number;
};

export type CreateSourceOptions = {
  bus?: AudioBusId;
  volume?: number;
  loop?: boolean;
  position?: vec3 | number[];
  attachTo?: AudioSourceTarget;
};

/**
 * Engine-level audio facade. Owns the AudioContext, master + bus graph, the
 * clip library, the voice pool, and the listener adapter. One per Engine.
 *
 * Lifecycle:
 *   const audio = new AudioEngine({ resolver });
 *   audio.resumeOnUserGesture(canvas);    // start AudioContext on first input
 *   audio.update(camera, dt);              // per-frame
 *   audio.playOneShot(clipId, { position, bus });
 *   audio.stopAll();                       // on scene deactivate
 *   audio.destroy();                       // on engine teardown
 *
 * Decoupled from Engine itself: tests can construct an AudioEngine with a mock
 * AudioContext and exercise the public API without any rendering plumbing.
 */
export class AudioEngine {
  public readonly library: AudioClipLibrary;
  private readonly ctx: AudioContext;
  private readonly masterGain: GainNode;
  private readonly busGains: Record<AudioBusId, GainNode>;
  private readonly listener: AudioListener;
  private readonly pool: VoicePool;
  private readonly sources = new Set<AudioSource>();
  /** Tracks one-shot voices that have an attachTo follower, so we can update positions each frame. */
  private readonly attachedOneShots = new Map<Voice, AudioSourceTarget>();
  private readonly tmpPos = vec3.create();
  private settingsState: AudioSettings;
  private gestureBound = false;
  private destroyed = false;

  constructor(init: AudioEngineInit) {
    this.ctx = init.ctx ?? createDefaultContext();
    this.library = init.library ?? new AudioClipLibrary(this.ctx, init.resolver);
    this.settingsState = mergeAudioSettings(defaultAudioSettings(), init.settings ?? {});

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = clampVolume(this.settingsState.masterVolume);
    this.masterGain.connect(this.ctx.destination);

    const buses: Partial<Record<AudioBusId, GainNode>> = {};
    for (const id of AUDIO_BUS_IDS) {
      const g = this.ctx.createGain();
      g.gain.value = clampVolume(this.settingsState.busVolumes[id]);
      g.connect(this.masterGain);
      buses[id] = g;
    }
    this.busGains = buses as Record<AudioBusId, GainNode>;

    this.listener = new AudioListener(this.ctx);
    this.pool = new VoicePool(this.settingsState.maxVoices);
  }

  /** Returns the underlying AudioContext. Most callers should not need this. */
  get audioContext(): AudioContext {
    return this.ctx;
  }

  get settings(): Readonly<AudioSettings> {
    return this.settingsState;
  }

  /** Apply a settings patch and propagate to the live graph. */
  applySettings(patch: Partial<AudioSettings>): void {
    this.settingsState = mergeAudioSettings(this.settingsState, patch);
    const now = this.ctx.currentTime;
    this.masterGain.gain.setValueAtTime(clampVolume(this.settingsState.masterVolume), now);
    for (const id of AUDIO_BUS_IDS) {
      this.busGains[id].gain.setValueAtTime(clampVolume(this.settingsState.busVolumes[id]), now);
    }
    this.pool.setGlobalCap(this.settingsState.maxVoices);
    // hrtfEnabled affects future voices; existing voices keep their current model.
  }

  setBusVolume(bus: AudioBusId, volume: number): void {
    this.applySettings({
      busVolumes: { ...this.settingsState.busVolumes, [bus]: clampVolume(volume) } as AudioBusVolumes,
    });
  }

  /**
   * Attach a one-time pointerdown/keydown listener to resume the AudioContext.
   * Browsers require a user gesture before audio output is allowed; until this
   * fires, all playOneShot / source.play calls produce silent output.
   */
  resumeOnUserGesture(target: HTMLElement | Document | Window): void {
    if (this.gestureBound) return;
    if (this.ctx.state === 'running') return;
    this.gestureBound = true;
    const handler = (): void => {
      this.ctx.resume().catch(() => {
        /* user denied or context already running */
      });
      target.removeEventListener('pointerdown', handler);
      target.removeEventListener('keydown', handler);
      target.removeEventListener('touchstart', handler);
    };
    target.addEventListener('pointerdown', handler, { once: true });
    target.addEventListener('keydown', handler, { once: true });
    target.addEventListener('touchstart', handler, { once: true });
  }

  /**
   * Fire-and-forget short SFX. Returns null if the clip isn't preloaded yet
   * (caller's responsibility to acquire it before triggering hot-path fires).
   */
  playOneShot(clipId: string, opts: PlayOneShotOptions = {}): Voice | null {
    if (this.destroyed) return null;
    const clip = this.library.getCached(clipId);
    if (!clip) return null;
    if (!this.pool.reserve(clipId, clip.config.maxConcurrent)) return null;

    const bus: AudioBusId = opts.bus ?? 'sfx';
    const busInput = this.busGains[bus];
    let position: vec3 | null = null;
    if (opts.attachTo) {
      const p = opts.attachTo.position;
      this.tmpPos[0] = p[0];
      this.tmpPos[1] = p[1];
      this.tmpPos[2] = p[2];
      position = vec3.clone(this.tmpPos);
    } else if (opts.position) {
      position = vec3.fromValues(opts.position[0], opts.position[1], opts.position[2]);
    }

    const voice = new Voice(this.ctx, clip, {
      bus,
      busInput,
      volume: opts.volume ?? 1.0,
      pitch: opts.pitch ?? 1.0,
      position,
      loop: false,
      hrtfEnabled: this.settingsState.hrtfEnabled,
    });
    this.pool.track(voice);

    if (opts.attachTo) {
      this.attachedOneShots.set(voice, opts.attachTo);
      voice.onEnded(() => this.attachedOneShots.delete(voice));
    }
    return voice;
  }

  /**
   * Long-lived spatial source. Async because the clip must be loaded before the
   * source can play. Resolves once the clip is in the library; the source is
   * returned in stopped state — call `.play()` to start.
   */
  async createSource(clipId: string, opts: CreateSourceOptions): Promise<AudioSource> {
    const clip = await this.library.acquire(clipId);
    return this.buildSource(clip, opts);
  }

  /**
   * Sync sibling of {@link createSource} — returns null if the clip isn't
   * already in the library cache. Caller is responsible for preloading
   * (via `library.acquire`) at scene-load. Useful for the composite path
   * where children are constructed synchronously alongside meshes/lights.
   */
  createSourceFromCached(clipId: string, opts: CreateSourceOptions): AudioSource | null {
    const clip = this.library.getCached(clipId);
    if (!clip) return null;
    return this.buildSource(clip, opts);
  }

  private buildSource(clip: import('./AudioClip').AudioClip, opts: CreateSourceOptions): AudioSource {
    const bus: AudioBusId = opts.bus ?? 'ambient';
    const position = opts.position
      ? vec3.fromValues(opts.position[0], opts.position[1], opts.position[2])
      : null;
    const source = new AudioSource(this.ctx, clip, {
      bus,
      busInput: this.busGains[bus],
      volume: opts.volume ?? 1.0,
      loop: opts.loop ?? false,
      position,
      attachTo: opts.attachTo ?? null,
      hrtfEnabled: this.settingsState.hrtfEnabled,
    });
    this.sources.add(source);
    return source;
  }

  /**
   * Per-frame tick. Updates listener from the active camera and pushes attached
   * follower positions into any tracked AudioSources / one-shot followers.
   */
  update(camera: Camera, _dt: number): void {
    if (this.destroyed) return;
    this.listener.update(camera);

    for (const source of this.sources) {
      if (source.destroyed) {
        this.sources.delete(source);
        continue;
      }
      source.update();
    }

    if (this.attachedOneShots.size > 0) {
      for (const [voice, target] of this.attachedOneShots) {
        if (voice.ended) {
          this.attachedOneShots.delete(voice);
          continue;
        }
        const p = target.position;
        this.tmpPos[0] = p[0];
        this.tmpPos[1] = p[1];
        this.tmpPos[2] = p[2];
        voice.setPosition(this.tmpPos);
      }
    }
  }

  /** Stop every voice + source. Called on scene deactivate. */
  stopAll(): void {
    this.pool.stopAll();
    this.attachedOneShots.clear();
    for (const s of this.sources) s.destroy();
    this.sources.clear();
  }

  /** Tear down. After this, the AudioEngine is unusable. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopAll();
    try {
      this.masterGain.disconnect();
      for (const id of AUDIO_BUS_IDS) this.busGains[id].disconnect();
    } catch {
      /* already disconnected */
    }
    // Closing the context releases hardware resources; ignore errors (already closed).
    this.ctx.close?.().catch(() => undefined);
  }

  /**
   * Test/diagnostics: live one-shot voice count. Long-lived `AudioSource`s
   * are owner-managed outside the pool — query `sourceCount` for those.
   */
  get voiceCount(): number {
    return this.pool.size;
  }

  /** Test/diagnostics: live AudioSource count. */
  get sourceCount(): number {
    return this.sources.size;
  }
}

function createDefaultContext(): AudioContext {
  // Avoid bare `window` so the engine module remains framework-agnostic and
  // safe to import from headless/test paths. Callers in non-browser contexts
  // (CLI, vitest) must inject `ctx` via AudioEngineInit; this fallback only
  // runs in real browsers where the global is defined.
  const g = globalThis as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const ctor = g.AudioContext ?? g.webkitAudioContext;
  if (!ctor) {
    throw new Error('AudioContext is not available in this environment');
  }
  return new ctor();
}
